/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { isEmpty } from 'lodash'

import {
  applyRenderTemplate
} from '../../utils'

export const MetricsRows = ({ objectId, metricSourceId, latestMetrics, metricToCompare, metricGroupToCompare, metricFamilyToCompare,
  metricResults, availableMetricDateIndex, dateRange, renderTemplate, onRowClick }) => {

  let latestMetricsResult = latestMetrics
    ? latestMetrics.result
    : null

  if (latestMetricsResult) {
    let props = { metricResults, latestMetricsResult, metricToCompare, metricGroupToCompare, metricFamilyToCompare, dateRange, renderTemplate }
    return (
      <Rows {...props} onClick={onRowClick} />
    )
  } else {
    return <tr></tr>
  }
}

MetricsRows.propTypes = {
  objectId: PropTypes.string,
  metricSourceId: PropTypes.string.isRequired,
  latestMetrics: PropTypes.shape({
    result: PropTypes.object.isRequired,
    date: PropTypes.string.isRequired
  }),
  metricToCompare: PropTypes.string.isRequired,
  metricFamilyToCompare: PropTypes.string,
  metricResults: PropTypes.array.isRequired,
  dateRange: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableMetricDateIndex: PropTypes.number.isRequired,
}

const displayMetricValue = (metricResult, family, group, metric) => {
  let metricValue = null
  
  try {
    metricValue = family === group && group === metric
      ? metricResult[family]
      : group === metric
        ? metricResult[family][metric]
        : metricResult[family][group][metric]
  } catch (e) {
    // if anything goes wrong display '-' (e.g. metricResult !== null && !isEmpty(metricResult))
    metricValue = '-'
  }

  return typeof metricValue === 'object' && isEmpty(metricValue)
    ? '-'
    : metricValue
}

const Rows = ({ metricResults, latestMetricsResult, metricToCompare, metricGroupToCompare, metricFamilyToCompare, dateRange, renderTemplate, onClick }) => {
  let rows = []

  // get latest available metrics, if they exist, apply render template in order to display metric values per field type
  let metricResult = latestMetricsResult
    ? applyRenderTemplate(latestMetricsResult, renderTemplate)
    : null

  // populate table rows and columns when specific metric family is chosen or not
  if (metricFamilyToCompare && metricGroupToCompare && metricToCompare) {
    rows.push(
      <tr
        onClick={() => onClick(metricFamilyToCompare, metricGroupToCompare, metricToCompare, metricResults, dateRange)}
        className='clickable'
        key={`family_${metricFamilyToCompare}_group_${metricGroupToCompare}_metric_${metricToCompare}`}>
        <td>
          {metricFamilyToCompare}
        </td>
        <td>
          {metricGroupToCompare === metricToCompare
            ? '-'
            : metricGroupToCompare
          }
        </td>
        <td>
          {metricToCompare}
        </td>
        {/* now populate values per date in date range */}
        {dateRange.map(date => {
          let metricsOnDate = metricResults.find(result => (
            moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
          ))

          let metricResult = metricsOnDate
            ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
            : null

          return (
            <td>
              {displayMetricValue(metricResult, metricFamilyToCompare, metricGroupToCompare, metricToCompare)}
            </td>
          )
        })}
      </tr>
    )
  } else if (metricFamilyToCompare && metricGroupToCompare) {
    let metricGroup = metricResult[metricFamilyToCompare][metricGroupToCompare]
    Object.keys(metricGroup).forEach(metric => (
      rows.push(
        <tr
          onClick={() => onClick(metricFamilyToCompare, metricGroupToCompare, metric, metricResults, dateRange)}
          className='clickable'
          key={`family_${metricFamilyToCompare}_group_${metricGroupToCompare}_metric_${metric}`}>
          <td>
            {metricFamilyToCompare}
          </td>
          <td>
            {metricGroupToCompare}
          </td>
          <td>
            {metric}
          </td>
          {/* now populate values per date in date range */}
          {dateRange.map(date => {
            let metricsOnDate = metricResults.find(result => (
              moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
            ))
            let metricResult = metricsOnDate
              ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
              : null

              return (
                <td>
                  {displayMetricValue(metricResult, metricFamilyToCompare, metricGroupToCompare, metric)}
                </td>
              )
          })}
        </tr>
      )
    ))
  } else if (metricFamilyToCompare && metricToCompare) {
    rows.push(
      <tr
        onClick={() => onClick(metricFamilyToCompare, null, metricToCompare, metricResults, dateRange)}
        className='clickable'
        key={`family_${metricFamilyToCompare}_metric_${metricToCompare}`}>
        <td>
          {metricFamilyToCompare}
        </td>
        <td>
          -
        </td>
        <td>
          {metricToCompare}
        </td>
        {/* now populate values per date in date range */}
        {dateRange.map(date => {
          let metricsOnDate = metricResults.find(result => (
            moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
          ))
          let metricResult = metricsOnDate
            ? applyRenderTemplate(
                metricsOnDate.result[metricFamilyToCompare][metricToCompare],
                renderTemplate[metricFamilyToCompare][metricToCompare]
              )
            : null

          return (
            <td>
              {metricResult[metricFamilyToCompare]
                ? metricResult[metricFamilyToCompare][metricToCompare]
                : '-'
              }
            </td>
          )
        })}
      </tr>
    )
  } else if (metricFamilyToCompare && typeof metricResult[metricFamilyToCompare] === 'object') {
    let metricFamily = metricResult[metricFamilyToCompare]
    if (metricFamily) {
      Object.keys(metricFamily).forEach(metricGroup => {
        if (typeof metricFamily[metricGroup] === 'object') {
          Object.keys(metricFamily[metricGroup]).forEach(metric => {
            rows.push(
              <tr
                onClick={() => onClick(metricFamilyToCompare, metricGroup, metric, metricResults, dateRange)}
                className='clickable'
                key={`family_${metricFamilyToCompare}_group_${metricGroupToCompare}_metric_${metric}`}>
                <td>
                  {metricFamilyToCompare}
                </td>
                <td>
                  {metricGroup}
                </td>
                <td>
                  {metric}
                </td>
                {/* now populate values per date in date range */}
                {dateRange.map(date => {
                  let metricsOnDate = metricResults.find(result => (
                    moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
                  ))
                  let metricResult = metricsOnDate
                    ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
                    : null

                    return (
                      <td>
                        {displayMetricValue(metricResult, metricFamilyToCompare, metricGroup, metric)}
                      </td>
                    )
                })}
              </tr>
            )
          })
        } else {
          rows.push(
            <tr
              onClick={() => onClick(metricFamilyToCompare, null, metricGroup, metricResults, dateRange)}
              className='clickable'
              key={`family_${metricFamilyToCompare}_group_${metricGroup}`}>
              <td>
                {metricFamilyToCompare}
              </td>
              <td>
                -
              </td>
              <td>
                {metricGroup}
              </td>
              {/* now populate values per date in date range */}
              {dateRange.map(date => {
                let metricsOnDate = metricResults.find(result => (
                  moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
                ))
                let metricResult = metricsOnDate
                  ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
                  : null

                  return (
                    <td>
                      {displayMetricValue(metricResult, metricFamilyToCompare, metricGroup, metricGroup)}
                    </td>
                  )
              })}
            </tr>
          )
        }
      })
    }
  } else if (metricFamilyToCompare) {
    rows.push(
      <tr
        onClick={() => onClick(metricFamilyToCompare, null, null, metricResults, dateRange)}
        className='clickable'
        key={`family_${metricFamilyToCompare}_group_${metricGroupToCompare}`}>
        <td>
          -
        </td>
        <td>
          -
        </td>
        <td>
          {metricFamilyToCompare}
        </td>
        {/* now populate values per date in date range */}
        {dateRange.map(date => {
          let metricsOnDate = metricResults.find(result => (
            moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
          ))
          let metricResult = metricsOnDate
            ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
            : null

            return (
              <td>
                {displayMetricValue(metricResult, metricFamilyToCompare, metricFamilyToCompare, metricFamilyToCompare)}
              </td>
            )
        })}
      </tr>
    )
  } else if (typeof metricResult === 'object') {
    if (isEmpty(metricResult)) {
      rows.push(
        <tr key={`family_${metricFamilyToCompare}_no-results`} style={{ color: '#fd5952' }}>
          <td colSpan={4}>
            No metrics for chosen frequency within date range.
          </td>
        </tr>
      )
    } else {
      Object.keys(metricResult).forEach(metricFamilyName => {
        let metricFamily = metricResult[metricFamilyName]
        if (typeof metricFamily === 'object') {
          Object.keys(metricFamily).forEach(metricGroupName => {
            let metricGroup = metricFamily[metricGroupName]
            if (typeof metricGroup === 'object') {
              Object.keys(metricGroup).forEach(metricName => {
                rows.push(
                  <tr
                    onClick={() => onClick(metricFamilyName, metricGroupName, metricName, metricResults, dateRange)}
                    className='clickable'
                    key={`family_${metricFamilyName}_group_${metricGroupName}_metric_${metricName}`}>
                    <td>
                      {metricFamilyName}
                    </td>
                    <td>
                      {metricGroupName}
                    </td>
                    <td>
                      {metricName}
                    </td>
                    {/* now populate values per date in date range */}
                    {dateRange.map(date => {
                      let metricsOnDate = metricResults.find(result => (
                        moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
                      ))
                      let metricResult = metricsOnDate
                        ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
                        : null

                        return (
                          <td>
                            {displayMetricValue(metricResult, metricFamilyName, metricGroupName, metricName)}
                          </td>
                        )
                    })}
                  </tr>
                )
              })
            } else {
              rows.push(
                <tr
                  onClick={() => onClick(metricFamilyName, null, metricGroupName, metricResults, dateRange)}
                  className='clickable'
                  key={`family_${metricFamilyName}_group_${metricGroupName}`}>
                  <td>
                    {metricFamilyName}
                  </td>
                  <td>
                    -
                  </td>
                  <td>
                    {metricGroupName}
                  </td>
                  {/* now populate values per date in date range */}
                  {dateRange.map(date => {
                    let metricsOnDate = metricResults.find(result => (
                      moment(result.date).isSame(moment(date, 'DD/MM/YYYY'), 'day')
                    ))
                    let metricResult = metricsOnDate
                      ? applyRenderTemplate(metricsOnDate.result, renderTemplate)
                      : null

                      return (
                        <td>
                          {displayMetricValue(metricResult, metricFamilyName, metricGroupName, metricGroupName)}
                        </td>
                      )
                  })}
                </tr>
              )
            }
          })
        }
      })
    }
  }

  return rows
}



// WEBPACK FOOTER //
// ./src/components/metric-comparison-table/MetricsRows.js